import { Fragment, useCallback, useEffect, useMemo, type ReactNode } from 'react';
import {
  buildPlanStarter,
  catalogBySlug,
  defaultRestSecFor,
  estimateTodayMinutes,
  fitTodayRows,
  parseTodayRestHours,
  regapTodayRows,
  repickTodayRows,
  shuffleTodayRow,
  shuffleTodayRows,
  todayAtRest,
  todayChainSets,
  TODAY_DEFAULT_INTENSITY,
  TODAY_DEFAULT_MINUTES,
  TODAY_INTENSITY_CHOICES,
  TODAY_MINUTE_CHOICES,
  todayPlannedEntries,
  DEFAULT_REST_HOURS,
  type Exercise,
  type PlanStarter,
  type Profile,
  type TodayDraftRow,
  type TodayProposalView,
  type TodayRestHours,
} from '@heraion/shared';

import type { LoggerHostDeps } from './hostDeps';
import { hydrateFromPlanStarter } from './planStarterHydration';
import type { LoggerDispatch } from './state/loggerDraft';
import { isTodayDraftPending } from './state/loggerDraftHelpers';
import type { LoggerDraft, WeightUnit } from './state/loggerDraftTypes';
import { useTodayEmpty } from './useTodayEmpty';
import { TodayDraftCard } from './TodayDraftCard';
import type { TodayDraftControls } from './TodayDraftHeader';
import type { Theme } from './theme';

// AP-9c: the unstarted «today» draft card and the edits it takes before Start
// (the time budget, the shuffles). Every edit rebuilds the draft's entries
// through the same chain a start runs (buildPlanStarter, then the plan
// hydration), so reps and kg always come from the anchor of the exercise the
// row now holds. Split out of TimelineLoggerScreen, over 350. P0 57d74690:
// the intensity chips scale the gaps (D5) and «Surprise me» picks again
// (D8), both saved to the profile like the minutes (D6). Antenore 07/10: the
// rest chips filter the build's own candidates here, like the minutes; the
// host saves the choice.

type Args = {
  draft: LoggerDraft;
  dispatch: LoggerDispatch;
  catalog: Exercise[] | null;
  defaultUnit: WeightUnit;
  /** The rest default, and Profile.todaySessionMinutes, todaySessionIntensity
   *  and todaySessionSurprise: the last choice, saved like the rest default
   *  (patchProfile, then refresh). */
  deps: Pick<LoggerHostDeps, 'profile' | 'patchProfile' | 'refreshProfile'>;
  resolveName: (slug: string, snapshot?: string) => string;
  onStart: () => void;
  onDiscard: () => void | Promise<boolean>;
  onOpenPainMap?: () => void;
  /** The empty card's «Build one anyway»: the host builds the draft and hands
   *  the starter back (null: nothing to build), when the draft's own
   *  candidates leave it empty. Absent: no button. */
  onBuildAnyway?: () => Promise<PlanStarter | null>;
  /** The empty card's «New workout»: the host's create hub. Absent: no button. */
  onOpenCreateHub?: () => void;
  /** A rest chip was tapped: the host saves the account's choice. */
  onRestHours?: (hours: TodayRestHours) => void;
  weeklyMinimumMet?: boolean;
  /** True while the draft is pending and has no entry: the host loads the
   *  coverage behind «Build one anyway» only then. */
  onEmptyChange?: (empty: boolean) => void;
  theme: Theme;
};

/** The draft card while the «today» draft waits for Start; undefined after. */
export function useTodayDraft({
  draft,
  dispatch,
  catalog,
  defaultUnit,
  deps,
  resolveName,
  onStart,
  onDiscard,
  onOpenPainMap,
  onBuildAnyway,
  onOpenCreateHub,
  onRestHours,
  weeklyMinimumMet,
  onEmptyChange,
  theme,
}: Args): ReactNode | undefined {
  const view = draft.todayProposal;
  const pending = isTodayDraftPending(draft);
  const profileMinutes = deps.profile.todaySessionMinutes;
  const storedMinutes =
    profileMinutes !== undefined &&
    (TODAY_MINUTE_CHOICES as ReadonlyArray<number>).includes(profileMinutes)
      ? profileMinutes
      : TODAY_DEFAULT_MINUTES;
  const profileIntensity = deps.profile.todaySessionIntensity;
  const storedIntensity =
    profileIntensity !== undefined &&
    (TODAY_INTENSITY_CHOICES as ReadonlyArray<number>).includes(profileIntensity)
      ? profileIntensity
      : TODAY_DEFAULT_INTENSITY;
  const storedSurprise = deps.profile.todaySessionSurprise === true;
  const profileDefaultRestSec = deps.profile.defaultRestSec;
  // A draft saved before AP-9c has no alternatives: shown as it is.
  const editable =
    view !== undefined && view.rows.every((r) => Array.isArray(r.alternatives));
  // LEGACY (TodayAlternative.last): no week to scale the gaps on. A draft
  // the rest left empty reads its candidates.
  const tuned =
    view !== undefined &&
    [...view.rows, ...(view.candidates ?? [])].some((r) => r.targetSets !== undefined);
  const index = useMemo(() => catalogBySlug(catalog ?? []), [catalog]);

  const restSecOf = useCallback(
    (slug: string) =>
      defaultRestSecFor(
        catalog?.find((e) => e.slug === slug)?.defaultRestSec,
        profileDefaultRestSec,
      ),
    [catalog, profileDefaultRestSec],
  );

  // D3 to D5, then the budget: the one path every chip takes.
  // A draft that ignored rest fills the time most rested first.
  const inOrder = view?.ignoreRest === true;
  const tune = useCallback(
    (rows: TodayDraftRow[], minutes: number, intensity: number, ordered = inOrder) =>
      fitTodayRows(regapTodayRows(rows, intensity, index), minutes, restSecOf, intensity, ordered),
    [index, restSecOf, inOrder],
  );

  const write = useCallback(
    (
      rows: TodayDraftRow[],
      minutes: number,
      tuning: Pick<TodayProposalView, 'intensity' | 'surprise'> = {},
      // The rest chips: the view the rest left.
      base: TodayProposalView | undefined = view,
    ) => {
      if (!view || !base || !catalog) return;
      const todayProposal = { ...base, ...tuning, minutes, rows };
      const starter = buildPlanStarter({
        todayProposal,
        entries: todayPlannedEntries(rows),
        ctx: {
          currentMaxes: new Map(),
          bodyweightKg: null,
          lastLoggedSets: todayChainSets(rows),
          catalogItems: catalog,
        },
      });
      const fresh = hydrateFromPlanStarter(starter, defaultUnit, catalog).entries;
      // A row whose exercise and sets did not change keeps what was typed.
      const entries = fresh.map(
        (e) =>
          draft.entries.find(
            (d) => d.exerciseId === e.exerciseId && d.sets.length === e.sets.length,
          ) ?? { ...e, location: draft.location ?? null },
      );
      // An exercise the athlete added by hand is not a row: it stays. A row
      // with 0 sets owns no entry, so a match on it stays too.
      const added = draft.entries.filter(
        (d) => !view.rows.some((r) => r.sets > 0 && r.exerciseId === d.exerciseId),
      );
      dispatch({
        type: 'hydrate',
        draft: { ...draft, entries: [...entries, ...added], todayProposal },
      });
    },
    [view, catalog, defaultUnit, draft, dispatch],
  );

  const empty = useTodayEmpty({
    draft,
    pending,
    catalog,
    defaultUnit,
    dispatch,
    onDiscard,
    ...(onBuildAnyway ? { onBuildAnyway } : {}),
    ...(onOpenCreateHub ? { onOpenCreateHub } : {}),
    ...(onEmptyChange ? { onEmptyChange } : {}),
  });

  // A fresh draft comes from the starter unfitted: fit it to the profile's
  // budget before the card shows it.
  const unfitted = pending && editable && view?.minutes === undefined && catalog !== null;
  useEffect(() => {
    if (!unfitted || !view) return;
    const rows = tuned && storedSurprise ? repickTodayRows(view.rows, Math.random) : view.rows;
    write(
      // A legacy draft ignores the profile's intensity: it opens at 100%.
      tune(rows, storedMinutes, tuned ? storedIntensity : TODAY_DEFAULT_INTENSITY),
      storedMinutes,
      tuned ? { intensity: storedIntensity, surprise: storedSurprise } : {},
    );
  }, [unfitted, tuned, storedMinutes, storedIntensity, storedSurprise, view, write, tune]);

  if (!view || !pending) return undefined;
  // Holds the slot (so no FocusPanel takes it) without the unfitted rows.
  if (unfitted) return <Fragment />;
  const minutes = view.minutes ?? storedMinutes;
  const intensity = view.intensity ?? TODAY_DEFAULT_INTENSITY;
  const surprise = view.surprise === true;
  // Best effort, like the rest default: the draft already holds the choice.
  const persist = (patch: Partial<Profile>) => {
    void deps.patchProfile(patch).then(deps.refreshProfile).catch(() => undefined);
  };
  // The rest chips and the local «anyway»: the build's own candidates at
  // another rest, then the same fit as the minutes. Rows that stay keep their
  // exercise; muscles that come back pick as «Surprise me» says.
  const candidates = view.candidates;
  const atRest = (restHours: number, ignoreRest: boolean) => {
    if (!candidates) return undefined;
    return todayAtRest(candidates, {
      nowMs: Date.now(),
      restHours,
      ignoreRest,
      held: view.rows,
      ...(surprise ? { random: Math.random } : {}),
    });
  };
  const applyRest = (next: ReturnType<typeof todayAtRest>) => {
    const { ignoreRest: _drop, ...base } = view;
    const ordered = next.ignoreRest === true;
    write(tune(next.rows, minutes, intensity, ordered), minutes, {}, { ...base, ...next });
  };
  // «Anyway» ignored rest: no chip is selected, and either one applies.
  const restKept = view.ignoreRest ? null : parseTodayRestHours(String(view.restHours));
  // Empty, and the build's candidates fill it with rest ignored: no host needed.
  const anyway =
    draft.entries.length === 0 && !view.ignoreRest
      ? atRest(view.restHours ?? DEFAULT_REST_HOURS, true)
      : undefined;
  const localAnyway = anyway && anyway.rows.length > 0 ? () => applyRest(anyway) : undefined;
  const onBuildAnywayNow = localAnyway ?? empty.onBuildAnyway;
  const controls: TodayDraftControls | undefined = editable
    ? {
        minutes,
        onMinutes: (m) => {
          persist({ todaySessionMinutes: m });
          write(tune(view.rows, m, intensity), m);
        },
        estimatedMin: estimateTodayMinutes(
          view.rows.map((r) => ({ sets: r.sets, restSec: restSecOf(r.exerciseId) })),
        ),
        onShuffleRow: (exerciseId) => {
          const idx = view.rows.findIndex((r) => r.exerciseId === exerciseId);
          if (idx >= 0) write(shuffleTodayRow(view.rows, idx), minutes);
        },
        onShuffleAll: () => write(shuffleTodayRows(view.rows), minutes),
        ...(tuned
          ? {
              tuning: {
                intensity,
                onIntensity: (pct: number) => {
                  persist({ todaySessionIntensity: pct });
                  write(tune(view.rows, minutes, pct), minutes, { intensity: pct });
                },
                surprise,
                onSurprise: (on: boolean) => {
                  persist({ todaySessionSurprise: on });
                  const rows = repickTodayRows(view.rows, on ? Math.random : undefined);
                  write(tune(rows, minutes, intensity), minutes, { surprise: on });
                },
              },
            }
          : {}),
        ...(candidates
          ? {
              rest: {
                hours: restKept,
                // The chip already selected changes nothing.
                onHours: (h: TodayRestHours) => {
                  if (h === restKept) return;
                  onRestHours?.(h);
                  const next = atRest(h, false);
                  if (next) applyRest(next);
                },
              },
            }
          : {}),
      }
    : undefined;

  return (
    <TodayDraftCard
      proposal={view}
      entries={draft.entries}
      dispatch={dispatch}
      resolveName={resolveName}
      units={defaultUnit}
      {...(controls ? { controls } : {})}
      onStart={onStart}
      onDiscard={onDiscard}
      {...(onOpenPainMap ? { onOpenPainMap } : {})}
      {...(onBuildAnywayNow ? { onBuildAnyway: onBuildAnywayNow } : {})}
      {...(empty.onOpenCreateHub ? { onOpenCreateHub: empty.onOpenCreateHub } : {})}
      building={empty.building}
      weeklyMinimumMet={weeklyMinimumMet === true}
      theme={theme}
    />
  );
}
